import { useAssessmentSlideOver } from "@components/campaigns/AssessmentSlideOver";
import {
  RECOMMENDATION_COLORS,
  RECOMMENDATION_LABELS,
} from "@components/campaigns/CandidateAssessmentSummary";
import { useReturnToPool } from "@components/campaigns/useReturnToPool";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { RadarChart } from "@components/charts/RadarChart";
import { ExportButton } from "@components/exports/ExportButton";
import { QueryErrorState } from "@components/shared/QueryErrorState";
import {
  ADVANCE_CAMPAIGN_STAGE,
  EXPORT_SHORTLIST_REPORT_TO_GOOGLE_DOCS,
  MOVE_CANDIDATE_STAGE,
} from "@lib/graphql/mutations";
import { ASSESSMENT_STAGE_QUERY, SHORTLIST_CANDIDATES_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { PIPELINE_STAGES, TERMINAL_STAGES } from "@lib/types";
import { CheckCircle2, Trophy } from "lucide-react";
import { useCallback } from "react";
import { useMutation, useQuery } from "urql";
import { StageBadge } from "./StageBadge";
import { StageDropdown, TerminalStageMoveControl } from "./StageDropdown";

interface ShortlistStageProps {
  campaignId: string;
}

interface CompetencyScoreData {
  id: string;
  score: number | null;
  evidence: string | null;
  notes: string | null;
  detailedNotes: string | null;
  capabilityDimension: {
    id: string;
    continuityId: string;
    name: string;
    description: string | null;
    idealScore: number | null;
  };
}

interface AssessmentVersionData {
  overallScore: number | null;
  recommendation: string | null;
  overallSummary: string | null;
  strengths: string | null;
  concerns: string | null;
  culturalFitNotes: string | null;
  trajectoryNotes: string | null;
  riskNotes: string | null;
  versionNumber: number;
  effectiveFrom: string;
  assessor: { id: string; name: string };
  competencyScores: CompetencyScoreData[];
}

interface CampaignCandidateData {
  id: string;
  stage: string;
  candidate: {
    id: string;
    fullName: string;
    referenceNumber?: number;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
  };
  candidateAssessment: {
    currentVersion: AssessmentVersionData | null;
  } | null;
}

interface DimensionData {
  id: string;
  continuityId: string;
  name: string;
  idealScore: number | null;
}

function scoreColor(score: number): string {
  if (score <= 1) return "text-rose-700 dark:text-rose-400";
  if (score === 2) return "text-amber-700 dark:text-amber-400";
  if (score === 3) return "text-emerald-600 dark:text-emerald-400";
  return "text-emerald-800 dark:text-emerald-300";
}

function buildScoresMap(competencyScores: CompetencyScoreData[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const cs of competencyScores) {
    if (cs.score != null) map[cs.capabilityDimension.continuityId] = cs.score;
  }
  return map;
}

const SHORTLIST_INDEX = PIPELINE_STAGES.indexOf("shortlist");

// "Moved on" from the shortlist means advanced past it (interview/offer/placed)
// or in a terminal stage (rejected/withdrawn). Candidates parked back to an
// earlier pipeline stage are not moved on — they live on the earlier tab.
function isMovedOnFromShortlist(stage: string): boolean {
  if (stage === "shortlist") return false;
  if ((TERMINAL_STAGES as readonly string[]).includes(stage)) return true;
  const idx = (PIPELINE_STAGES as readonly string[]).indexOf(stage);
  return idx > SHORTLIST_INDEX;
}

export function ShortlistStage({ campaignId }: ShortlistStageProps) {
  // Fetch role blueprint dimensions for comparison table
  const [{ data: blueprintData }] = useQuery({
    query: ASSESSMENT_STAGE_QUERY,
    variables: { id: campaignId },
  });

  const dimensions: DimensionData[] =
    blueprintData?.campaign?.roleBlueprint?.capabilityDimensions ?? [];

  // Fetch shortlist candidates with assessments (shared panel fragment)
  const [{ data, fetching, error }, reexecute] = useQuery({
    query: SHORTLIST_CANDIDATES_QUERY,
    variables: { id: campaignId },
  });
  const [, moveStage] = useMutation(MOVE_CANDIDATE_STAGE);
  const [, advanceStage] = useMutation(ADVANCE_CAMPAIGN_STAGE);
  const [, exportShortlistReport] = useMutation(EXPORT_SHORTLIST_REPORT_TO_GOOGLE_DOCS);

  const allCandidates: CampaignCandidateData[] = data?.campaign?.campaignCandidates ?? [];
  const activeCandidates = allCandidates.filter((cc) => cc.stage === "shortlist");
  const movedOnCandidates = allCandidates.filter((cc) => isMovedOnFromShortlist(cc.stage));
  const currentStage = data?.campaign?.currentStage;

  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const { openFor: openReturnToPool, dialog: returnToPoolDialog } = useReturnToPool(refresh);

  const { open: openAssessment, slideOverElement: assessmentSlideOver } = useAssessmentSlideOver({
    candidates: allCandidates,
    dimensions,
  });

  const handleMove = async (id: string, stage: string) => {
    await moveStage({ input: { campaignCandidateId: id, stage } });
    refresh();
  };

  const handleDeliver = async () => {
    if (currentStage && currentStage <= 6) {
      await advanceStage({
        input: { id: campaignId, targetStage: 7, reason: "manual:deliver_shortlist" },
      });
      refresh();
    }
  };

  const handleExportStart = async () => {
    const result = await exportShortlistReport({ input: { campaignId } });
    const payload = result.data?.exportShortlistReportToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  if (fetching && allCandidates.length === 0) {
    return <p className="text-sm text-text-muted">Loading shortlist...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-text-faint" />
            <h3 className="text-sm font-medium text-text-secondary">Shortlist</h3>
            <span className="text-xs text-text-faint">
              {activeCandidates.length} active
              {movedOnCandidates.length > 0 && `, ${movedOnCandidates.length} moved on`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {allCandidates.length > 0 && (
              <ExportButton
                onStart={handleExportStart}
                className="flex items-center gap-1 rounded border border-input-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
              />
            )}
            {activeCandidates.length > 0 && (
              <button
                type="button"
                onClick={handleDeliver}
                className="flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90"
              >
                <CheckCircle2 size={14} /> Deliver Shortlist
              </button>
            )}
          </div>
        </div>

        {error ? (
          <QueryErrorState
            error={error}
            what="the shortlist"
            onRetry={refresh}
            retrying={fetching}
          />
        ) : allCandidates.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-faint">
            No candidates shortlisted yet. Move candidates from the Assessment stage.
          </p>
        ) : (
          <>
            {/* Active Candidate Cards */}
            {activeCandidates.length > 0 && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {activeCandidates.map((cc) => {
                  const assessment = cc.candidateAssessment?.currentVersion;
                  const scoresMap = assessment ? buildScoresMap(assessment.competencyScores) : {};
                  return (
                    <div
                      key={cc.id}
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer rounded-lg border border-border p-4 hover:ring-2 hover:ring-accent/50"
                      onClick={() => openAssessment(cc.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openAssessment(cc.id);
                        }
                      }}
                    >
                      <div className="mb-2 flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-text-primary">
                            {typeof cc.candidate.referenceNumber === "number" && (
                              <>
                                <CandidateReferenceBadge
                                  referenceNumber={cc.candidate.referenceNumber}
                                />{" "}
                              </>
                            )}
                            {cc.candidate.fullName}
                          </h4>
                          {(cc.candidate.currentRoleTitle || cc.candidate.currentCompany) && (
                            <p className="text-xs text-text-muted">
                              {[cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
                                .filter(Boolean)
                                .join(" at ")}
                            </p>
                          )}
                          {cc.candidate.location && (
                            <p className="text-xs text-text-faint">{cc.candidate.location}</p>
                          )}
                        </div>
                        <StageDropdown
                          currentStage="shortlist"
                          onMove={(stage) => handleMove(cc.id, stage)}
                          onReturnToPool={() => openReturnToPool(cc, "shortlist")}
                        />
                      </div>

                      {assessment ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            {assessment.overallScore != null && (
                              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                {assessment.overallScore}/5
                              </span>
                            )}
                            {assessment.recommendation && (
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${RECOMMENDATION_COLORS[assessment.recommendation] ?? ""}`}
                              >
                                {RECOMMENDATION_LABELS[assessment.recommendation]}
                              </span>
                            )}
                          </div>

                          {/* Radar chart per candidate */}
                          {assessment.competencyScores.length > 0 && dimensions.length > 0 && (
                            <RadarChart
                              dimensions={dimensions}
                              candidateScores={scoresMap}
                              candidateName={cc.candidate.fullName}
                              size="sm"
                            />
                          )}

                          {assessment.strengths && (
                            <div>
                              <span className="text-[10px] font-medium text-text-muted">
                                Strengths
                              </span>
                              <p className="line-clamp-2 text-xs text-text-secondary">
                                {assessment.strengths}
                              </p>
                            </div>
                          )}

                          {assessment.concerns && (
                            <div>
                              <span className="text-[10px] font-medium text-text-muted">
                                Concerns
                              </span>
                              <p className="line-clamp-2 text-xs text-text-secondary">
                                {assessment.concerns}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-text-faint">No assessment data</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Moved-on candidates */}
            {movedOnCandidates.length > 0 && (
              <div className={activeCandidates.length > 0 ? "mt-4" : ""}>
                <h4 className="mb-2 text-xs font-medium text-text-muted">Moved On</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {movedOnCandidates.map((cc) => {
                    const assessment = cc.candidateAssessment?.currentVersion;
                    const scoresMap = assessment ? buildScoresMap(assessment.competencyScores) : {};
                    return (
                      <div
                        key={cc.id}
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer rounded-lg border border-border bg-card-hover/30 p-4 hover:ring-2 hover:ring-accent/50"
                        onClick={() => openAssessment(cc.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openAssessment(cc.id);
                          }
                        }}
                      >
                        <div className="mb-2 flex items-start justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-text-primary">
                              {typeof cc.candidate.referenceNumber === "number" && (
                                <>
                                  <CandidateReferenceBadge
                                    referenceNumber={cc.candidate.referenceNumber}
                                  />{" "}
                                </>
                              )}
                              {cc.candidate.fullName}
                            </h4>
                            {(cc.candidate.currentRoleTitle || cc.candidate.currentCompany) && (
                              <p className="text-xs text-text-muted">
                                {[cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
                                  .filter(Boolean)
                                  .join(" at ")}
                              </p>
                            )}
                            {cc.candidate.location && (
                              <p className="text-xs text-text-faint">{cc.candidate.location}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <StageBadge stage={cc.stage} />
                            <TerminalStageMoveControl
                              stage={cc.stage}
                              onMove={(stage) => handleMove(cc.id, stage)}
                            />
                          </div>
                        </div>

                        {assessment ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              {assessment.overallScore != null && (
                                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                  {assessment.overallScore}/5
                                </span>
                              )}
                              {assessment.recommendation && (
                                <span
                                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${RECOMMENDATION_COLORS[assessment.recommendation] ?? ""}`}
                                >
                                  {RECOMMENDATION_LABELS[assessment.recommendation]}
                                </span>
                              )}
                            </div>

                            {assessment.competencyScores.length > 0 && dimensions.length > 0 && (
                              <RadarChart
                                dimensions={dimensions}
                                candidateScores={scoresMap}
                                candidateName={cc.candidate.fullName}
                                size="sm"
                              />
                            )}

                            {assessment.strengths && (
                              <div>
                                <span className="text-[10px] font-medium text-text-muted">
                                  Strengths
                                </span>
                                <p className="line-clamp-2 text-xs text-text-secondary">
                                  {assessment.strengths}
                                </p>
                              </div>
                            )}

                            {assessment.concerns && (
                              <div>
                                <span className="text-[10px] font-medium text-text-muted">
                                  Concerns
                                </span>
                                <p className="line-clamp-2 text-xs text-text-secondary">
                                  {assessment.concerns}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-text-faint">No assessment data</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Comparison Table — all candidates who reached shortlist */}
            {dimensions.length > 0 &&
              allCandidates.some((cc) => cc.candidateAssessment?.currentVersion) && (
                <div className="mt-6">
                  <h4 className="mb-2 text-xs font-medium text-text-secondary">Comparison</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="pb-2 pr-4 text-left font-medium text-text-muted">
                            Dimension
                          </th>
                          {dimensions.some((d) => d.idealScore != null) && (
                            <th className="pb-2 text-center font-bold text-text-secondary">
                              BEST FIT
                            </th>
                          )}
                          {allCandidates.map((cc) => (
                            <th
                              key={cc.id}
                              className="pb-2 text-center font-medium text-text-primary"
                            >
                              {typeof cc.candidate.referenceNumber === "number" && (
                                <>
                                  <CandidateReferenceBadge
                                    referenceNumber={cc.candidate.referenceNumber}
                                  />{" "}
                                </>
                              )}
                              {cc.candidate.fullName}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dimensions.map((dim) => (
                          <tr key={dim.id} className="border-b border-border">
                            <td className="py-1.5 pr-4 text-text-secondary">{dim.name}</td>
                            {dimensions.some((d) => d.idealScore != null) && (
                              <td className="py-1.5 text-center font-medium text-text-secondary">
                                {dim.idealScore ?? "-"}
                              </td>
                            )}
                            {allCandidates.map((cc) => {
                              const cs =
                                cc.candidateAssessment?.currentVersion?.competencyScores.find(
                                  (s) => s.capabilityDimension.continuityId === dim.continuityId,
                                );
                              return (
                                <td key={cc.id} className="py-1.5 text-center">
                                  {cs?.score != null ? (
                                    <span
                                      className={`inline-block rounded bg-gray-100 px-2 py-0.5 font-medium dark:bg-gray-800 ${scoreColor(cs.score)}`}
                                    >
                                      {cs.score}
                                    </span>
                                  ) : (
                                    <span className="text-text-faint">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        <tr className="border-t border-border font-medium">
                          <td className="py-1.5 pr-4 text-text-secondary">Overall</td>
                          {dimensions.some((d) => d.idealScore != null) && <td />}
                          {allCandidates.map((cc) => {
                            const overall = cc.candidateAssessment?.currentVersion?.overallScore;
                            return (
                              <td key={cc.id} className="py-1.5 text-center text-text-primary">
                                {overall != null ? `${overall}/5` : "-"}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </>
        )}
      </div>

      {assessmentSlideOver}
      {returnToPoolDialog}
    </div>
  );
}
